import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import classNames from "classnames";
import FocusLock from "react-focus-lock";
import { useSwipeable } from "react-swipeable";
import { CSSTransition } from "react-transition-group";

import useWindowSize from "hooks/useWindowSize";

import CloseIcon from "components/Icons/x.svg";

import { useModalContext } from "./internal/ModalContext";
import { useModalRenderContext } from "./internal/ModalRenderContext";

const SWIPE_TOLERANCE = 150; // px
const SWIPE_RESET_DURATION = 100; // ms

export const BASE_Z_INDEX = 140;

const MOBILE_BREAKPOINT = 765; // px
const TRANSITION_DURATION_TRANSFORM = 350; // ms
const TRANSITION_DURATION_OPACITY = 200; // ms

const noop = () => {
  // do nothing
};

export type ModalProps = {
  /**
   * An aria-labelledby property is required for accessibility, unless a title
   * is supplied. This should be the ID of an element in the modal which labels
   * the modal; this element will be read first by screen readers when the modal
   * becomes visible.
   */
  "aria-labelledby"?: string;
  /**
   * Children can either be one or more React nodes, or a function that returns
   * React nodes. If a function is provided, the single argument is an object
   * containing the property "dismiss" -- a function that can be called to
   * dismiss the modal. E.g.:
   *
   *   <InformationalModal ...>
   *     {({ dismiss }) => <button onClick={dismiss} />}
   *   <InformationalModal>
   */
  children?:
    | React.ReactNode
    | ((args: { dismiss: () => void }) => React.ReactNode);
  /**
   * Useful for disabling swiping when modal contains scrollable elements.
   */
  disableSwipeToClose?: boolean;
  /**
   * When viewed on mobile, occupy the entire viewport. Default: true.
   */
  mobileFullscreen?: boolean;
  /**
   * Callback fired when the modal has dismissed and is no longer visible.
   */
  onDismissed?: () => void;
  /**
   * React ref that points to the DOM element that should have focus returned
   * when the modal is closed. Almost always this should be the button that
   * was clicked that resulted in the modal appearing. This is required in
   * order to maintain good accessibility standards, where a screenreader
   * will need to understand to where the text cursor should be returned after
   * the modal is dismissed.
   */
  returnFocusRef?: React.RefObject<HTMLElement>;
  /**
   * Allows a secondary action element to be placed into the modal header, to
   * the left of the title. For example, the addition of a back button for multi-screen modals.
   */
  secondaryAction?: JSX.Element;
  /**
   * The title that is displayed at the top of the modal and that labels the
   * modal for ARIA compliance. If omitted, the `aria-labelledby` property
   * must be supplied instead.
   */
  title?: string | JSX.Element;
  /**
   * Set a custom width for the modal on desktop. On mobile devices, the modal
   * will grow to fit the screen size.
   */
  width?: number;
};

/**
 * Modal renders a generic modal interface. This component should be used
 * as a basis for defining other shared modal components for specific uses,
 * such as InformationalModal. Applications should almost always prefer the
 * use of the derivative, use-case–specific modal components, rather than
 * using Modal directly. If you're using this component, consult with your
 * designer to ensure that there isn't a way to use a standardized pattern
 * instead of creating a custom implementation.
 *
 * For general modal usage, see the sibling index module's documentation.
 */
export const Modal: React.FC<ModalProps> = ({
  "aria-labelledby": ariaLabelledBy,
  children,
  disableSwipeToClose = false,
  mobileFullscreen = true,
  onDismissed = noop,
  returnFocusRef,
  secondaryAction,
  title,
  width,
}) => {
  if (!title && !ariaLabelledBy) {
    throw new Error(
      "InformationalModal must be supplied either a title or an aria-labelledby prop"
    );
  }

  /**
   * Assigns a unique ID to this instance of the Modal component, which is used
   * to signal to the ModalContext when the modal appears or is dismissed.
   */
  const id = useMemo(Symbol, []);

  /**
   * Creates a unique DOM ID to be used to label the modal with the provided
   * title, when supplied.
   */
  const titleId = useId();

  const { register, unregister, isPrimary, modalIndexOf, __testing } =
    useModalContext();
  const modalRenderContext = useModalRenderContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [resettingSwipe, setResettingSwipe] = useState(false);
  const { width: windowWidth } = useWindowSize();

  /**
   * isVisible is set to null by default in order to signal a pre-visible state
   * which is toggled in the useEffect below after the next tick.
   * Unfortunately, this means that modal tests need to have a timeout to wait
   * for that next tick before interacting with the rendered content. To avoid
   * this, testing mode sets visibility to true immediately.
   */
  const [isVisible, setIsVisible] = useState<boolean | null>(__testing || null);

  const transitionDuration =
    windowWidth && windowWidth > MOBILE_BREAKPOINT
      ? TRANSITION_DURATION_OPACITY
      : TRANSITION_DURATION_TRANSFORM;

  const zIndex = BASE_Z_INDEX + modalIndexOf(id);

  useEffect(() => {
    /**
     * Allow the component to be mounted and then set to be visible on the next
     * tick, in order to allow the transitions to be applied after the initial
     * render has set the default styles.
     */
    const t = setTimeout(() => {
      setIsVisible(true);
    }, 1);

    /**
     * Record the container ref value here, as the reference to the element is
     * otherwise not available when the cleanup function of this useEffect is run.
     */
    const scrollLockTarget = containerRef.current;

    if (scrollLockTarget) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
      disableBodyScroll(scrollLockTarget as any, {
        /**
         * This option allows finer control over which elements can contine
         * to scroll when `disableBodyScroll` is invoked. In this case, any
         * element that is a child of the modal container should be allowed
         * to scroll on touch devices.
         */
        allowTouchMove: (el) => !!scrollLockTarget?.contains(el),
      });
    }
    /**
     * Signal to the ModalContext that a new modal has been mounted.
     */
    register(id);

    return () => {
      clearTimeout(t);

      if (scrollLockTarget) {
        enableBodyScroll(scrollLockTarget);
      }
      /**
       * Unregister here, even though this is also called in the next
       * useEffect, mostly to cover hot-reloading and Modal components being
       * unexpectedly yanked out of the tree without being dismissed. Calling
       * `unregister` redundantly has no effect, so this is safe.
       */
      unregister(id);
    };
  }, [id, register, unregister]);

  useEffect(() => {
    /**
     * Check for isVisible to be explicitly false here, as the initial state of
     * `null` is used as the component is mounting to signal a pre-visible state.
     */
    if (isVisible === false) {
      /**
       * Unregister the modal before the transition begins, in order to allow
       * the background overlay to transition out at the same time as the modal.
       */
      unregister(id);

      const t = setTimeout(() => {
        onDismissed();
      }, transitionDuration);

      return () => clearTimeout(t);
    }
    /**
     * TODO: The `onDismissed` callback should be added here as well, but adding it
     * causes infinite recursion. Some investigation is needed to figure how to
     * safely keep this prop up to date in the useEffect callback, but in common
     * use-cases it should be okay to omit for now.
     */
  }, [id, unregister, isVisible, transitionDuration]);

  const swipeHandlers = useSwipeable(
    disableSwipeToClose
      ? {}
      : {
          onSwipedDown: (event) => {
            if (
              !resettingSwipe &&
              event.deltaY > SWIPE_TOLERANCE &&
              /**
               * Ensure that there is not any scrollable content before dismissing
               * the modal. Only the modalRef needs to be checked, as this is the
               * only scrollable container on the mobile breakpoint.
               */
              modalRef?.current?.scrollTop &&
              modalRef.current.scrollTop === 0
            ) {
              setIsVisible(false);
            } else {
              setResettingSwipe(true);
              setSwipeOffset(0);

              setTimeout(() => {
                setResettingSwipe(false);
              }, SWIPE_RESET_DURATION);
            }
          },
          onSwiping: (event) => {
            if (!resettingSwipe) {
              setSwipeOffset(Math.max(0, event.deltaY));
            }
          },
        }
  );

  const dismiss = () => {
    setIsVisible(false);
  };

  // @ts-ignore (fix me please, do not replicate)
  const handleBackgroundClose = (e) => {
    /**
     * Calling stopPropagation shouldn't normally be required, but for some
     * reason if the modal component is mounted inside of a next.js Link
     * element this link still gets "clicked" when clicking on any anchor
     * or button element inside of the modal -- even though the modal is
     * rendered using a React portal. Stopping event bubbling here prevents
     * the next.js Link from being triggered.
     */
    e.stopPropagation();

    if (
      (e.type === "click" && e.target === containerRef.current) ||
      e.code === "Escape"
    ) {
      e.preventDefault();
      dismiss();
    }
  };

  const contentTransform = `translateY(${swipeOffset}px) ${
    !isPrimary(id) ? "scale(0.97)" : ""
  }`;

  return (
    modalRenderContext?.renderModal(
      <FocusLock
        /**
         * FocusLock adds elements to the DOM with tabindex != 0 in order to
         * detect when focus leaves the contained area. This is an accessibility
         * violation according to AXE. However, the objections to using non-zero
         * tabindex outlined in the documentation apply only to tabbing through
         * _visible_ elements on the page, which does not apply to the experience
         * provided by FocusLock. In order to allow axe testing of this modal
         * component, FocusLock is always disabled in testing mode.
         *
         * See: https://dequeuniversity.com/rules/axe/4.1/tabindex
         */
        disabled={__testing || !isPrimary(id)}
        onDeactivation={() => {
          if (returnFocusRef && returnFocusRef.current) {
            returnFocusRef.current.focus();
          }
        }}
      >
        <div
          className={classNames("modal-container", {
            "modal-container--fullscreen": mobileFullscreen,
          })}
          onClick={handleBackgroundClose}
          onKeyDown={handleBackgroundClose}
          ref={containerRef}
          role="presentation"
          style={{ zIndex }}
        >
          <CSSTransition
            in={!!isVisible}
            /**
             * This property prevents a one frame flash where the child of the
             * transition component is mounted but the "enter" class has not been
             * added. Usually this is not required when using this component, but
             * because there are multiple transitions when a modal enters the
             * viewport, likely a reflow is being forced between mount and the
             * "enter" class being applied. Using `mountOnEnter` makes the
             * element mount with the "enter" class already applied.
             */
            mountOnEnter={true}
            timeout={transitionDuration}
          >
            <div
              aria-hidden={!isPrimary(id)}
              aria-labelledby={title ? titleId : ariaLabelledBy}
              aria-modal={isPrimary(id)}
              /**
               * Caution: CSSTransition manipulates these classes using a ref
               * to the .modal element, which can override conditional classes
               * applied with classNames. If possible, avoid using conditional
               * classes on this element.
               */
              className="modal cursor-default text-default"
              data-testid="modal"
              ref={modalRef}
              role="dialog"
            >
              <section
                className={classNames(
                  "h-full modal-content bg-white elevation-low pb-lg px-lg",
                  {
                    "modal-content--resetting": resettingSwipe,
                    "modal-content--secondary": !isPrimary(id),
                    "modal-content--with-title": Boolean(title),
                  }
                )}
                style={{ transform: contentTransform }}
                /**
                 * These handlers need to be installed on the child element of
                 * `.modal`, as they add a ref that interferes with the ref
                 * installed on the parent.
                 */
                {...swipeHandlers}
              >
                <div
                  className={classNames("modal-header flex", {
                    "modal-header--sticky border-b border-light-grey": !!title,
                  })}
                >
                  {secondaryAction && secondaryAction}
                  {title && (
                    <h1
                      id={titleId}
                      className={classNames(
                        "title px-lg text-xs uppercase font-extrabold "
                      )}
                    >
                      <div className="title-truncation">{title}</div>
                    </h1>
                  )}

                  <button
                    aria-label="Close modal"
                    className="close-button absolute"
                    onClick={dismiss}
                    data-testid="close-modal"
                  >
                    <CloseIcon width={22} height={22} />
                  </button>
                </div>

                {children instanceof Function
                  ? children({ dismiss })
                  : children}
              </section>
            </div>
          </CSSTransition>
        </div>
        <style jsx>{`
          .modal-container {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            top: 0;
            cursor: default;
          }

          .modal-container:focus {
            outline: none;
          }

          .modal {
            margin: auto;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            width: auto;
            overflow-y: auto;
            max-height: 100%;
          }

          .modal-container--fullscreen .modal {
            top: 0;
          }

          .modal:focus {
            outline: none;
          }

          .modal.enter {
            transform: translate(0%, 100%);
          }

          .modal.enter-active,
          .modal.enter-done,
          .modal.exit {
            transform: translate(0%, 0%);
            transition: transform ${TRANSITION_DURATION_TRANSFORM}ms ease-in-out;
          }

          .modal.exit-active,
          .modal.exit-done {
            transform: translate(0%, 100%);
          }

          .modal-content {
            padding-top: 40px;
            min-height: 100%;
            width: 100%;
            overflow-y: auto;
            position: relative;
            transition: transform ${TRANSITION_DURATION_TRANSFORM}ms ease-in-out,
              filter ${TRANSITION_DURATION_TRANSFORM}ms ease-in-out;
          }

          .modal-content::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            background-color: rgba(0, 0, 0, 0.7);
            opacity: 0;
            pointer-events: none;
            transition: opacity ${TRANSITION_DURATION_TRANSFORM}ms ease-in-out;
          }

          .modal-content--secondary {
            filter: blur(0.5px);
          }

          .modal-content--secondary::after {
            opacity: 0.7;
          }

          .modal-content--resetting {
            transition: transform ${SWIPE_RESET_DURATION}ms ease-in;
          }

          .modal-content--with-title {
            padding-top: 64px;
          }

          .modal-header {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            background-color: white;
            z-index: 60;
          }

          .close-button {
            top: 14px;
            right: 14px;
          }

          .title {
            padding-top: 14px;
            padding-bottom: 14px;
          }

          .title-truncation {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            padding-right: 30px;
          }

          @supports (position: sticky) {
            .modal-header--sticky {
              position: sticky;
              top: 0;
              left: unset;
              right: unset;
              will-change: transform;
              margin: 0 -16px 16px;
            }

            .modal-content--with-title {
              padding-top: 0;
            }
          }

          @media screen and (min-width: 768px) {
            .modal-container {
              overflow-y: scroll;
            }

            .modal {
              height: auto;
              width: ${width}px;
              min-height: auto;
              top: 15vh;
              position: relative;
              bottom: auto;
              padding-bottom: 5vh;
              overflow-y: unset;
            }

            .modal-container--fullscreen .modal {
              top: 15vh;
            }

            .modal-header {
              border-radius: 4px;
            }

            .modal-content {
              border-radius: 4px;
            }
          }

          @media (min-width: 768px), (prefers-reduced-motion) {
            .modal {
              transform: none !important;
            }

            .modal.enter {
              opacity: 0;
            }

            .modal.enter-active,
            .modal.enter-done,
            .modal.exit {
              opacity: 1;
              transition: opacity ${TRANSITION_DURATION_OPACITY}ms ease-in-out;
            }

            .modal.exit-active,
            .modal.exit-done {
              opacity: 0;
            }
          }
        `}</style>
      </FocusLock>
    ) || <></>
  );
};
